import React, { useState } from "react";
import { Route, Routes, Navigate } from "react-router-dom";

import ModalImpago from "../Components/AvisoImpago/ModalImpago";
import useImpagoChecker from "../hook/useImpagoChecker";

import Footer from "../Components/Footer/Footer";
import AdminNavbar from "../Components/Navbars/AdminNavbar";
import Sidebar from "../Components/Sidebar/Sidebar";

import Dashboard from "../Page/Dashboard";
import Pacientes from "../Page/Pacientes";
import HistorialClinico from "../Page/HistorialClinico";
import GestionCitas from "../Page/GestionCitas";
import SigVital from "../Page/SigVital";
import MiAgenda from "../Page/MiAgenda";
import FacturaListar from "../Page/FacturaListar";
import FacturaListarPorCobrar from "../Page/FacturaListarPorCobrar";
import FacturaCrear from "../Page/FacturaCrear";
import Configuracion from "../Page/Configuracion";
// SEGUROS-PLANES: rutas con el seguro en la URL (ver el bloque de rutas abajo).
import SegurosLista from "../Page/Seguros/SegurosLista";
import SeguroFicha from "../Page/Seguros/SeguroFicha";
import SeguroResumen from "../Page/Seguros/SeguroResumen";
import SeguroTarifario from "../Page/Seguros/SeguroTarifario";
import SeguroPlanes from "../Page/Seguros/SeguroPlanes";
import SeguroPromociones from "../Page/Seguros/SeguroPromociones";
import PlanConstructor from "../Page/Seguros/PlanConstructor";
import Clientes from "../Page/Clientes";
import CajaGeneral from "../Page/Reportes/CajaGeneral/CajaGeneral";
import MatrizAtenciones from "../Page/Reportes/Reportes Varios/MatrizAtenciones";
import ReporteCoberturas from "../Page/Reportes/ReporteCoberturas";
import ReporteProduccionFacturacion from "../Page/Reportes/ReporteProduccionFacturacion";
import ReporteCartera from "../Page/Reportes/ReporteCartera";
import ReporteAnulados from "../Page/Reportes/ReporteAnulados";
import ResultadoExamen from "../Page/ResultadoExamanes";
import ApiDocsViewer from "../Page/ApiDocs";
import Soporte from "../Page/Soporte";
import AuditoriaClinicaIndex from "../Page/AuditoriaClinica/AuditoriaClinicaIndex";
import AuditoriaAtencionDetalle from "../Page/AuditoriaClinica/AuditoriaAtencionDetalle";
import AuditoriaBandeja from "../Page/AuditoriaClinica/AuditoriaBandeja";

// ✅ Ajusta la ruta real
import usePermissions from "../hook/usePermissions";
import useNavCollapse, { NAV_BREAKPOINT_MOVIL } from "../hook/useNavCollapse";
import { PageHeaderProvider } from "../context/PageHeaderContext";
import { ToastProvider } from "../Components/UI/Aviso";
// Definicion UNICA de "usuario administrativo" (antes duplicada aqui y en
// Sidebar.js). Mismos permisos, mismo comportamiento.
import { ADMIN_AREA_PERMISSIONS } from "../utils/permisosAdmin";

export default function Admin() {
  const [collaseSidebar, setCollapseSidebar] = useState(true);
  const { showModal, setShowModal } = useImpagoChecker();

  // REDISENO-SMARTMEDIC FASE 2 (Eje 2): el boton hamburguesa ahora hace DOS
  // cosas distintas segun el ancho, que es lo que el usuario espera:
  //   <992px  -> el tema oculta el menu; el boton abre/cierra el overlay
  //              (`sidebar-enable`), comportamiento de SIDEBAR-MENU-UX-FIX-01.
  //   >=992px -> alterna expandido (248px) / rail (70px). Antes NO hacia nada
  //              en escritorio: el handler del tema se ataba por jQuery a
  //              `#vertical-menu-btn` antes de que React montara el boton.
  const { nav, alternarNav } = useNavCollapse();
  const toggleMenu = () => {
    if (typeof window !== "undefined" && window.innerWidth < NAV_BREAKPOINT_MOVIL) {
      document.body.classList.toggle("sidebar-enable");
      return;
    }
    alternarNav();
  };

  // ✅ Componente protector simple
  const DoctorOnlyRedirect = ({ children }) => {
    return children;
  };

  // RBAC-CUTOVER-08: BlockForDoctor (isDoctor/nivel_user_id) retirado --
  // su ultimo consumidor vivo (/admin/api-docs) ya usa
  // RequirePermissionRoute permission="platform.api_docs.view".

  // RBAC-CUTOVER-03: pantalla terminal -- NUNCA redirige a otra ruta
  // guardada. Redirigir un guard denegado hacia OTRA ruta guardada (p.ej.
  // /pacientes -> /sigvital) es un bug real de ping-pong infinito para
  // cualquier actor sin ninguno de los dos permisos (no solo teorico: hoy
  // ningun rol real cae en ese caso, pero un guard no puede depender de eso
  // para no colgar el navegador). Un panel estatico corta la cadena siempre.
  const NoAutorizado = () => (
    <div className="page-content">
      <div className="container-fluid">
        <div className="alert alert-warning" role="alert">
          No tiene permisos para acceder a esta sección.
        </div>
      </div>
    </div>
  );

  // RBAC-CUTOVER-02 seccion 12: el menu (Sidebar) ya no basta -- alguien
  // podria navegar directo a la URL. `hasPermission` es fail-closed mientras
  // `loading` (usePermissions.js), asi que no se renderiza nada hasta
  // conocer el permission set real (evita un redirect prematuro en falso).
  // El backend sigue siendo la autoridad final (RequirePermission).
  const RequirePermissionRoute = ({ permission, children }) => {
    const { loading, hasPermission } = usePermissions();
    if (loading) return null;
    return hasPermission(permission) ? children : <NoAutorizado />;
  };

  // CITAS-VISTA-MEDICO: a dónde va quien entra por la raíz. Cada quien
  // aterriza en la agenda que PUEDE abrir, decidido por el permiso igual que
  // el menú y las propias rutas.
  //
  // Espera a que el permission set esté cargado, como las dos de arriba. Sin
  // esa espera, `hasPermission` es fail-closed mientras carga y el médico
  // saldría disparado a /gestion_citas -- una pantalla que además no puede
  // abrir -- antes de que se sepa lo que tiene.
  const InicioSegunPermiso = () => {
    const { loading, hasPermission } = usePermissions();
    if (loading) return null;
    return <Navigate to={hasPermission("citas.mi_agenda.view") ? "/agenda" : "/gestion_citas"} replace />;
  };

  // RBAC-CUTOVER-03: variante para rutas sin UN permiso propio (p.ej.
  // /configuracion, que agrupa varios modulos distintos) -- permite el paso
  // si el actor tiene AL MENOS UNO de los permisos reales indicados. Mismo
  // criterio fail-closed mientras carga.
  const RequireAnyPermissionRoute = ({ permissions, children }) => {
    const { loading, hasPermission } = usePermissions();
    if (loading) return null;
    return permissions.some((p) => hasPermission(p)) ? children : <NoAutorizado />;
  };

  // RBAC-CUTOVER-03/07: mismo catalogo usado en Sidebar.js para decidir
  // acceso a /configuracion -- ver comentario ahi (nota: `clinics.view`
  // singular queda fuera a proposito, DOCTOR tambien lo tiene). RBAC-CUTOVER-07
  // agrega los *.view de los modulos que cierran el PERMISSION_CATALOG_GAP
  // (Doctores/Especialidades/Seguros/Tarifarios/Promociones/Planes) -- sin
  // esto, un CUSTOM_ROLE con solo, p.ej., doctors.view nunca podria entrar a
  // /configuracion aunque su tab si le corresponda. Ninguno inventado.

  return (
    <PageHeaderProvider>
    <ToastProvider>
    <div id="layout-wrapper">
      <AdminNavbar
        collaseSidebar={collaseSidebar}
        setCollapseSidebar={setCollapseSidebar}
        toggleMenu={toggleMenu}
      />
      <Sidebar collaseSidebar={collaseSidebar} nav={nav} />

      <div className="main-content">
        <Routes>
          {/* Inicio */}
          {/* CITAS-VISTA-MEDICO: cada quien aterriza en la agenda que puede
              abrir. Lo decide el PERMISO, igual que el menú y que las propias
              rutas: quien tiene `citas.mi_agenda.view` (el rol Doctor) va a la
              suya; el resto, a la general de siempre. */}
          <Route
            path="/"
            element={<InicioSegunPermiso />}
          />

          {/* ✅ Permitidas para doctor -- ahora ademas gateadas por permission real */}
          <Route
            path="/pacientes"
            element={
              <RequirePermissionRoute permission="pacientes.view">
                <Pacientes />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/paciente_historial"
            element={
              <RequirePermissionRoute permission="pacientes.view">
                <HistorialClinico />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/gestion_citas"
            element={
              <RequirePermissionRoute permission="citas.agenda.view">
                <GestionCitas />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/sigvital"
            element={
              <RequirePermissionRoute permission="signos_vitales.view">
                <SigVital />
              </RequirePermissionRoute>
            }
          />

          {/* RBAC-CUTOVER-06: Agenda migrada a citas.view real -- reemplaza
              BlockForDoctor (isDoctor/nivel_user_id), que dejaba pasar a
              SIGNOS_VITALES aunque no tuviera citas.view. El contenido
              clinico ya usaba citas.view; ahora el contenedor de ruta
              tambien. /admin/api-docs sigue sin permiso propio -- ver
              PERMISSION_CATALOG_GAP debajo, documentado para un corte
              posterior (sin dominio de negocio claro / posible dev-tool). */}
          {/* CITAS-VISTA-MEDICO: esta ruta servía una maqueta (FullCalendar
              con un evento "Meeting" inventado). Ahora es la agenda del propio
              médico, con permiso propio `citas.mi_agenda.view` -- que sólo
              tiene el rol Doctor y no se puede asignar a mano. */}
          <Route
            path="/agenda"
            element={
              <RequirePermissionRoute permission="citas.mi_agenda.view">
                <MiAgenda />
              </RequirePermissionRoute>
            }
          />
          {/* RBAC-CUTOVER-04: Facturacion migrada a billing.* real --
              reemplaza BlockForDoctor (isDoctor), que dejaba pasar a
              SIGNOS_VITALES (caso prioritario de esta fase). */}
          <Route
            path="/factura-listar"
            element={
              <RequirePermissionRoute permission="billing.view">
                <FacturaListar />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/factura-listar-por-cobrar"
            element={
              <RequirePermissionRoute permission="billing.view">
                <FacturaListarPorCobrar />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/factura-crear"
            element={
              <RequirePermissionRoute permission="billing.create">
                <FacturaCrear />
              </RequirePermissionRoute>
            }
          />
          {/* CLIENTES-MODULO-01: módulo de clientes de facturación. */}
          <Route
            path="/clientes"
            element={
              <RequirePermissionRoute permission="clients.view">
                <Clientes />
              </RequirePermissionRoute>
            }
          />
          {/* RBAC-CUTOVER-05: Reportes/Resultados/Soporte migrados a permisos
              reales -- reemplaza BlockForDoctor (isDoctor), que dejaba pasar
              a SIGNOS_VITALES. Matriz Atenciones es un reporte real (ver
              reportes.routes.ts/GetMatrizAtenciones), distinto de Matriz
              Seguros (billing.reports.view, ya migrado en CUTOVER-04). */}
          <Route
            path="/matrizatenciones"
            element={
              <RequirePermissionRoute permission="reportes.view">
                <MatrizAtenciones />
              </RequirePermissionRoute>
            }
          />
          {/* COBERTURAS-06: Reporte General de Atenciones -- vista ANALÍTICA
              de solo lectura, distinta del módulo operativo
              /factura-listar-por-cobrar (que crea/cancela/factura). */}
          <Route
            path="/reportes/coberturas"
            element={
              <RequirePermissionRoute permission="reportes.view">
                <ReporteCoberturas />
              </RequirePermissionRoute>
            }
          />
          {/* REPORTES-PRODUCCION-FACTURACION-10: reporte consolidado por
              prestación (paciente + seguro + copago + médico + Excel).
              Solo lectura. Mismo permiso reportes.view. */}
          <Route
            path="/reportes/produccion-facturacion"
            element={
              <RequirePermissionRoute permission="reportes.view">
                <ReporteProduccionFacturacion />
              </RequirePermissionRoute>
            }
          />
          {/* COBERTURAS-07: Cartera por Aseguradora -- vista ANALÍTICA de solo
              lectura (sin registro de cobros, ver context/22 §B). Mismo
              permiso reportes.view que /reportes/coberturas. */}
          <Route
            path="/reportes/cartera-aseguradoras"
            element={
              <RequirePermissionRoute permission="reportes.view">
                <ReporteCartera />
              </RequirePermissionRoute>
            }
          />
          {/* ANULACION-FISCAL-64: Documentos Anulados -- solo lectura.
              `reportes.view` para LEER lo anulado; la ACCION de anular una
              factura ya emitida vive en /facturas y exige el permiso aparte
              `billing.cancel_fiscal`. */}
          <Route
            path="/reportes/documentos-anulados"
            element={
              <RequirePermissionRoute permission="reportes.view">
                <ReporteAnulados />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/resultados"
            element={
              <RequirePermissionRoute permission="resultados.view">
                <ResultadoExamen />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/soporte"
            element={
              <RequirePermissionRoute permission="soporte.view">
                <Soporte />
              </RequirePermissionRoute>
            }
          />
          {/* AUDITORIA-CLINICA-REDISENO · cambio de guardia (FASE 5 paso 4):
              `/auditoria-clinica` sirve YA la bandeja rediseñada. El listado
              clásico queda sin entrada de menú, sólo accesible en
              `/auditoria-clinica/clasico` como red de seguridad mientras dura
              la verificación; cuando el usuario lo dé por bueno, se borra.
              Permiso propio `auditoria.clinica.view` -- nunca citas.view. */}
          <Route
            path="/auditoria-clinica"
            element={
              <RequirePermissionRoute permission="auditoria.clinica.view">
                <AuditoriaBandeja />
              </RequirePermissionRoute>
            }
          />
          {/* Los enlaces que se compartieron durante las pruebas siguen vivos. */}
          <Route path="/auditoria-clinica/bandeja" element={<Navigate to="/auditoria-clinica" replace />} />
          <Route
            path="/auditoria-clinica/clasico"
            element={
              <RequirePermissionRoute permission="auditoria.clinica.view">
                <AuditoriaClinicaIndex />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/auditoria-clinica/:idcita"
            element={
              <RequirePermissionRoute permission="auditoria.clinica.view">
                <AuditoriaAtencionDetalle />
              </RequirePermissionRoute>
            }
          />
          {/* RBAC-CUTOVER-03: reemplaza BlockForDoctor (isDoctor) -- ningun
              permiso unico cubre toda Configuracion, pero SI existe un
              conjunto real de permisos administrativos (ver
              ADMIN_AREA_PERMISSIONS) que distingue correctamente a
              MASTER/SUPERADMIN/ADMIN_COMPANY/ADMIN_CLINICA/LEGACY_ADMIN
              (pasan) de SIGNOS_VITALES/DOCTOR (no pasan, ninguno tiene
              ninguno de estos permisos) -- sin inventar un permiso nuevo ni
              hardcodear nombres de rol. */}
          <Route
            path="/configuracion"
            element={
              <RequireAnyPermissionRoute permissions={ADMIN_AREA_PERMISSIONS}>
                <Configuracion />
              </RequireAnyPermissionRoute>
            }
          />

          {/* ================================================================
              SEGUROS-PLANES FASE 1 · el seguro pasa a la ruta.

              El seguro es la entidad; tarifario, planes y promociones son
              caras suyas. Con el id en la URL, cada pantalla carga ya filtrada
              y nunca vuelve a preguntar cuál es el seguro -- hoy se pregunta
              tres veces.

              CONVIVEN con `/configuracion`, que no se toca: sus pestañas
              siguen ahí y siguen funcionando igual. Es la opción reversible
              que acordamos: nada se rompe mientras se valida lo nuevo. (No hay
              rutas legacy que redirigir: `/configuracion/planes` y compañía
              nunca existieron, las secciones eran estado interno del
              componente -- ver informe FASE 0 §6-R1.)

              Cada ruta lleva SU permiso, el que ya exige el backend. Sin esto
              un rol con solo `seguros.view` vería pestañas que el servidor le
              va a rechazar con 403. Ninguno es nuevo.

              El constructor de plan queda FUERA de la ficha a propósito: es
              pantalla completa, no una pestaña. */}
          <Route
            path="/configuracion/seguros"
            element={
              <RequirePermissionRoute permission="seguros.view">
                <SegurosLista />
              </RequirePermissionRoute>
            }
          />
          {/* Estas dos van ANTES de la ficha para que `planes/nuevo` no lo
              atienda la pestaña `planes`. */}
          <Route
            path="/configuracion/seguros/:idseguro/planes/nuevo"
            element={
              <RequirePermissionRoute permission="planes.create">
                <PlanConstructor />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/configuracion/seguros/:idseguro/planes/:idplan"
            element={
              <RequirePermissionRoute permission="planes.edit">
                <PlanConstructor />
              </RequirePermissionRoute>
            }
          />
          <Route
            path="/configuracion/seguros/:idseguro"
            element={
              <RequirePermissionRoute permission="seguros.view">
                <SeguroFicha />
              </RequirePermissionRoute>
            }
          >
            <Route index element={<SeguroResumen />} />
            <Route
              path="tarifario"
              element={
                <RequirePermissionRoute permission="procedimientosprecios.view">
                  <SeguroTarifario />
                </RequirePermissionRoute>
              }
            />
            <Route
              path="planes"
              element={
                <RequirePermissionRoute permission="planes.view">
                  <SeguroPlanes />
                </RequirePermissionRoute>
              }
            />
            <Route
              path="promociones"
              element={
                <RequirePermissionRoute permission="promociones.view">
                  <SeguroPromociones />
                </RequirePermissionRoute>
              }
            />
          </Route>

          {/* RBAC-CUTOVER-04: Caja General es Billing real (consume
              GetInvoiceCaja/Excel de Service.billing.js) -- billing.reports.view. */}
          <Route
            path="/caja-general"
            element={
              <RequirePermissionRoute permission="billing.reports.view">
                <CajaGeneral />
              </RequirePermissionRoute>
            }
          />
          {/* RBAC-CUTOVER-08: cierra el ultimo BlockForDoctor conocido --
              platform.api_docs.view reemplaza isDoctor/nivel_user_id (que
              dejaba pasar a SIGNOS_VITALES). ApiDocsViewer es documentacion
              tecnica estatica (sin llamada a backend propia); el
              @fastify/swagger real sigue gateado por NODE_ENV!=='production'
              en main.ts, sin relacion con esta ruta. */}
          <Route
            path="/admin/api-docs"
            element={
              <RequirePermissionRoute permission="platform.api_docs.view">
                <ApiDocsViewer />
              </RequirePermissionRoute>
            }
          />

          {/* ✅ Ruta fallback */}
          <Route path="*" element={<Navigate to="/gestion_citas" replace />} />
        </Routes>

        <Footer />
      </div>

      <ModalImpago show={showModal} onHide={() => setShowModal(false)} />
    </div>
    </ToastProvider>
    </PageHeaderProvider>
  );
}